<template>
  <div class="login">
    <div
      class="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8"
    >
      <div class="max-w-md w-full space-y-8 bg-card rounded-xl border border-border shadow-sm p-8">
        <div>
          <h2 class="mt-6 text-center text-3xl font-extrabold text-foreground">
            {{ $t('auth.signInTitle') }}
          </h2>
          <p
            v-if="settingsStore.anyRegistrationEnabled"
            class="mt-2 text-center text-sm text-muted-foreground"
          >
            {{ $t('auth.or') }}
            <router-link
              :to="localePath('Register')"
              class="font-medium text-primary hover:text-primary/80"
            >
              {{ $t('auth.createNewAccount') }}
            </router-link>
          </p>
        </div>

        <form class="mt-8 space-y-6" novalidate @submit.prevent="handleLogin">
          <AlertBanner v-if="sessionExpired" variant="warning">
            <p class="text-sm">{{ $t('auth.sessionExpired') }}</p>
          </AlertBanner>

          <AlertBanner v-if="confirmed" variant="success">
            <p class="text-sm">
              {{ $t('auth.emailConfirmed') }}
            </p>
          </AlertBanner>

          <AlertBanner v-if="oauthError" variant="error">
            <p class="text-sm">{{ oauthError }}</p>
          </AlertBanner>

          <AlertBanner v-if="error" variant="error">
            <p class="text-sm">{{ error }}</p>
          </AlertBanner>

          <div class="space-y-4">
            <div>
              <label for="email" class="block text-sm font-medium text-foreground mb-1">{{
                $t('auth.emailLabel')
              }}</label>
              <input
                id="email"
                v-model="email"
                type="email"
                :class="[
                  'appearance-none relative block w-full px-3 py-2 border placeholder-muted-foreground text-foreground rounded-md focus:outline-none sm:text-sm',
                  fieldErrors.email
                    ? 'border-destructive focus:ring-destructive focus:border-destructive'
                    : 'border-input bg-input-background focus:ring-ring focus:border-ring',
                ]"
                :placeholder="$t('auth.emailPlaceholder')"
              />
              <p v-if="fieldErrors.email" class="mt-1 text-sm text-destructive">
                {{ fieldErrors.email }}
              </p>
            </div>
            <div>
              <label for="password" class="block text-sm font-medium text-foreground mb-1">{{
                $t('auth.passwordLabel')
              }}</label>
              <input
                id="password"
                v-model="password"
                type="password"
                :class="[
                  'appearance-none relative block w-full px-3 py-2 border placeholder-muted-foreground text-foreground rounded-md focus:outline-none sm:text-sm',
                  fieldErrors.password
                    ? 'border-destructive focus:ring-destructive focus:border-destructive'
                    : 'border-input bg-input-background focus:ring-ring focus:border-ring',
                ]"
                :placeholder="$t('auth.passwordPlaceholder')"
              />
              <p v-if="fieldErrors.password" class="mt-1 text-sm text-destructive">
                {{ fieldErrors.password }}
              </p>
            </div>
          </div>

          <div class="flex items-center justify-end">
            <router-link
              to="/forgot-password"
              class="text-sm font-medium text-primary hover:text-primary/80"
            >
              {{ $t('auth.forgotPassword') }}
            </router-link>
          </div>

          <div>
            <button
              type="submit"
              :disabled="loading"
              class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <LoadingSpinner :loading="loading" />
              {{ $t('auth.signIn') }}
            </button>
          </div>

          <div v-if="settingsStore.anyOAuthEnabled" class="mt-6">
            <div class="relative">
              <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-border"></div>
              </div>
              <div class="relative flex justify-center text-sm">
                <span class="px-2 bg-background text-muted-foreground">{{
                  $t('auth.orContinueWith')
                }}</span>
              </div>
            </div>

            <div
              :class="['mt-6 grid gap-3', enabledOAuthCount > 1 ? 'grid-cols-2' : 'grid-cols-1']"
            >
              <button
                v-if="settingsStore.googleAuthEnabled"
                type="button"
                class="w-full inline-flex justify-center py-2 px-4 border border-border rounded-md shadow-sm bg-card text-sm font-medium text-foreground hover:bg-muted cursor-pointer"
                @click="handleOAuthLogin('google')"
              >
                <svg class="h-5 w-5" viewBox="0 0 48 48">
                  <path
                    fill="#EA4335"
                    d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                  />
                  <path
                    fill="#4285F4"
                    d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.0 24.0 0 0 0 0 21.56l7.98-6.19z"
                  />
                  <path
                    fill="#34A853"
                    d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                  />
                </svg>
              </button>
              <button
                v-if="settingsStore.facebookAuthEnabled"
                type="button"
                class="w-full inline-flex justify-center py-2 px-4 border border-border rounded-md shadow-sm bg-card text-sm font-medium text-foreground hover:bg-muted cursor-pointer"
                @click="handleOAuthLogin('facebook')"
              >
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../../stores/auth'
import { useSettingsStore } from '../../stores/settings'
import { useLocalePath } from '../../composables/useLocalePath'
import { usePageTitle } from '../../composables/usePageTitle'
import { initiateOAuth } from '../../utils/oauth'
import LoadingSpinner from '../../components/LoadingSpinner.vue'
import AlertBanner from '../../components/AlertBanner.vue'
import { safeRedirect } from '../../router/authRoutes'

const router = useRouter()
const route = useRoute()

const { t } = useI18n()
usePageTitle(() => t('titles.login'))
const confirmed = computed(() => route.query.confirmed === 'true')
const sessionExpired = computed(() => route.query.sessionExpired === 'true')
const OAUTH_ERROR_KEYS = {
  provider_disabled: 'auth.oauthProviderDisabled',
  auth_failed: 'auth.oauthAuthFailed',
  cancelled: 'auth.oauthCancelled',
}
const oauthError = computed(() => {
  const code = route.query.oauth_error
  if (!code) return null
  return t(OAUTH_ERROR_KEYS[code] || 'auth.oauthAuthFailed')
})
const authStore = useAuthStore()
const settingsStore = useSettingsStore()
const { localePath } = useLocalePath()

const enabledOAuthCount = computed(
  () => (settingsStore.googleAuthEnabled ? 1 : 0) + (settingsStore.facebookAuthEnabled ? 1 : 0),
)

onMounted(() => {
  settingsStore.fetchSettings()
})

const email = ref('')
const password = ref('')
const loading = ref(false)
const error = ref(null)
const fieldErrors = reactive({})

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const validate = () => {
  delete fieldErrors.email
  delete fieldErrors.password

  if (!email.value.trim()) {
    fieldErrors.email = t('auth.emailRequired')
  } else if (!emailRegex.test(email.value.trim())) {
    fieldErrors.email = t('auth.emailInvalid')
  }

  if (!password.value) {
    fieldErrors.password = t('auth.passwordRequired')
  }

  return Object.keys(fieldErrors).length === 0
}

watch(email, () => delete fieldErrors.email)
watch(password, () => delete fieldErrors.password)

const handleLogin = async () => {
  error.value = null
  if (!validate()) return

  loading.value = true

  try {
    await authStore.login(email.value, password.value)
    router.push(safeRedirect(router, route.query.redirect) || localePath('Home'))
  } catch (error_) {
    const isUnconfirmed =
      error_.response?.status === 403 && error_.response?.data?.error === 'unconfirmed'
    error.value = t(isUnconfirmed ? 'auth.emailNotConfirmed' : 'auth.loginFailed')
  } finally {
    loading.value = false
  }
}

const handleOAuthLogin = (provider) => {
  const redirect = safeRedirect(router, route.query.redirect)
  if (redirect) {
    sessionStorage.setItem('authRedirect', redirect)
  }
  initiateOAuth(provider)
}
</script>
